// src/components/SourcesTab.jsx
import React from 'react';

const SourcesTab = () => {
  return (
    <div className="space-y-6 p-4">
      <h2 className="text-xl font-medium text-gray-800">Data Sources</h2>
      
      <p className="text-gray-600">
        This application integrates data from multiple authoritative sources to provide comprehensive information about medicines registered in South Africa.
      </p>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
        {/* MPR Source Card */}
        <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm">
          <div className="bg-blue-50 px-4 py-3 border-b border-blue-100 flex items-center">
            <div className="flex-shrink-0 bg-blue-100 p-2 rounded-full">
              <i className="fas fa-money-bill-wave text-blue-500"></i>
            </div>
            <h3 className="ml-3 text-lg font-medium text-blue-800">Medicine Price Registry (MPR)</h3>
          </div>
          <div className="p-4">
            <p className="text-sm text-gray-600">
              The Medicine Price Registry (MPR) is the official registry of Single Exit Prices (SEP) of medicines in South Africa.
              It contains information about:
            </p>
            <ul className="mt-2 space-y-1 list-disc list-inside text-sm text-gray-600">
              <li>Medicine pricing (SEP)</li>
              <li>NAPPI codes</li>
              <li>Dosage forms</li>
              <li>Available generic options</li>
            </ul>
            <div className="mt-4 text-sm">
              <a 
                href="https://medicineprices.org.za/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 flex items-center"
              >
                Visit MPR Website <i className="fas fa-external-link-alt ml-1"></i>
              </a>
            </div>
          </div>
        </div>
        
        {/* SAHPRA Source Card */}
        <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm">
          <div className="bg-purple-50 px-4 py-3 border-b border-purple-100 flex items-center">
            <div className="flex-shrink-0 bg-purple-100 p-2 rounded-full">
              <i className="fas fa-clipboard-check text-purple-500"></i>
            </div>
            <h3 className="ml-3 text-lg font-medium text-purple-800">South African Health Products Regulatory Authority (SAHPRA)</h3>
          </div>
          <div className="p-4">
            <p className="text-sm text-gray-600">
              SAHPRA is the regulatory authority responsible for monitoring, evaluating, and registering all health products in South Africa.
              Their database provides:
            </p>
            <ul className="mt-2 space-y-1 list-disc list-inside text-sm text-gray-600">
              <li>Registration status</li>
              <li>Registration numbers</li>
              <li>Approval dates</li>
              <li>Active ingredients</li>
              <li>Manufacturer information</li>
              <li>Scheduling information</li>
            </ul>
            <div className="mt-4 text-sm">
              <a 
                href="https://www.sahpra.org.za/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-purple-600 hover:text-purple-800 flex items-center"
              >
                Visit SAHPRA Website <i className="fas fa-external-link-alt ml-1"></i>
              </a>
            </div>
          </div>
        </div>
        
        {/* Database Cache Card */}
        <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm">
          <div className="bg-green-50 px-4 py-3 border-b border-green-100 flex items-center">
            <div className="flex-shrink-0 bg-green-100 p-2 rounded-full">
              <i className="fas fa-database text-green-500"></i>
            </div>
            <h3 className="ml-3 text-lg font-medium text-green-800">Local Database Cache</h3>
          </div>
          <div className="p-4">
            <p className="text-sm text-gray-600">
              This application maintains a local database cache to:
            </p>
            <ul className="mt-2 space-y-1 list-disc list-inside text-sm text-gray-600">
              <li>Reduce API calls to external sources</li>
              <li>Improve search performance</li>
              <li>Merge data from multiple sources</li>
              <li>Provide search history</li>
              <li>Enable offline access to previously searched data</li>
            </ul>
            <p className="mt-3 text-sm text-gray-600">
              The cache can be configured in the search options, including cache expiry time and the ability to force fresh data retrieval.
            </p>
          </div>
        </div>
        
        {/* Data Usage Card */}
        <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm">
          <div className="bg-yellow-50 px-4 py-3 border-b border-yellow-100 flex items-center">
            <div className="flex-shrink-0 bg-yellow-100 p-2 rounded-full">
              <i className="fas fa-exclamation-triangle text-yellow-500"></i>
            </div>
            <h3 className="ml-3 text-lg font-medium text-yellow-800">Important Notice</h3>
          </div>
          <div className="p-4">
            <p className="text-sm text-gray-600">
              While this application strives to provide accurate information by integrating data from authoritative sources:
            </p>
            <ul className="mt-2 space-y-1 list-disc list-inside text-sm text-gray-600">
              <li>All information should be verified with official sources</li>
              <li>Data may not be completely up-to-date, especially when using cached results</li>
              <li>This tool is for informational purposes only</li>
              <li>For critical medical or regulatory decisions, always consult official documentation</li>
            </ul>
            <p className="mt-3 text-sm text-red-600 font-medium">
              Healthcare professionals should not rely solely on this tool for clinical decisions.
            </p>
            <p className="mt-3 text-sm text-gray-600">
              This is an independent project. It is not affiliated with, endorsed by, or acting on behalf of
              SAHPRA, the National Department of Health, or the Medicine Price Registry. All data remains the
              property of its respective owners and no ownership of it is claimed.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SourcesTab;